import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { adminAPI } from '../services/api';
import { Users, FolderOpen, Mail, TrendingUp, LogOut, Building2, FileText, BarChart3, UserCog, Network as NetworkIcon, Coins, Settings, Briefcase, Calculator } from 'lucide-react';
import { toast } from 'sonner';

export const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    if (!user || !['admin', 'editor'].includes(user.role)) {
      navigate('/admin/login');
      return;
    }
    if (isAdmin) {
      loadStats();
    } else {
      setLoading(false);
    }
  }, [user, navigate, isAdmin]);

  const loadStats = async () => {
    try {
      const response = await adminAPI.getStats();
      if (response.success) {
        setStats(response.stats);
      }
    } catch (error) {
      toast.error('Fehler beim Laden der Statistiken');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-gray-400">Lädt Dashboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-[#1a1a1a] to-black">
      {/* Header */}
      <header className="bg-[#1a1a1a] border-b border-[#C4A55A]/20 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Building2 className="h-8 w-8 text-[#C4A55A]" />
              <div>
                <h1 className="text-2xl font-bold text-white">Admin Dashboard</h1>
                <p className="text-sm text-gray-400">Löwenherz Invest AG</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-gray-400">Hallo, {user?.firstName}</span>
              <Button
                onClick={handleLogout}
                variant="outline"
                className="border-[#C4A55A]/30 text-[#C4A55A] hover:bg-[#C4A55A]/10"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Abmelden
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {/* Role Banner for editors */}
        {!isAdmin && (
          <div className="mb-6 rounded-xl bg-emerald-500/10 border border-emerald-500/30 px-4 py-3 text-sm text-emerald-300">
            Sie sind als <strong>Editor</strong> angemeldet — Zugriff auf Projekte und Downloads.
          </div>
        )}

        {/* Analytics Highlight Banner (Admin only) */}
        {isAdmin && (
          <button
            onClick={() => navigate('/admin/analytics')}
            data-testid="admin-dashboard-analytics-banner"
            className="w-full mb-8 group text-left rounded-2xl bg-gradient-to-r from-[#C4A55A]/20 via-[#1a1a1a] to-[#1a1a1a] border border-[#C4A55A]/40 hover:border-[#C4A55A]/80 p-6 transition-all duration-300 hover:shadow-lg hover:shadow-[#C4A55A]/20"
          >
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl bg-[#C4A55A]/20 border border-[#C4A55A]/40">
                  <BarChart3 className="h-7 w-7 text-[#C4A55A]" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                    Auswertungen & Analytics
                  </h2>
                  <p className="text-sm text-gray-400">
                    Investoren-Wachstum, Volumen, Anfragen, Downloads & mehr — alles auf einen Blick
                  </p>
                </div>
              </div>
              <div className="text-[#C4A55A] font-medium text-sm group-hover:translate-x-1 transition-transform">
                Ansehen →
              </div>
            </div>
          </button>
        )}

        {/* Stats Cards (Admin only) */}
        {isAdmin && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="bg-[#1a1a1a] border-[#C4A55A]/20 hover:border-[#C4A55A]/50 transition-all duration-300">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-gray-400">Investoren</CardTitle>
              <Users className="h-4 w-4 text-[#C4A55A]" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-white">{stats?.totalUsers || 0}</div>
              <p className="text-xs text-gray-500 mt-1">Registrierte Investoren</p>
            </CardContent>
          </Card>

          <Card className="bg-[#1a1a1a] border-[#C4A55A]/20 hover:border-[#C4A55A]/50 transition-all duration-300">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-gray-400">Investitionsvolumen</CardTitle>
              <TrendingUp className="h-4 w-4 text-[#C4A55A]" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-white">
                CHF {stats?.totalInvestment?.toLocaleString('de-CH') || 0}
              </div>
              <p className="text-xs text-gray-500 mt-1">Gesamtinvestitionen</p>
            </CardContent>
          </Card>

          <Card className="bg-[#1a1a1a] border-[#C4A55A]/20 hover:border-[#C4A55A]/50 transition-all duration-300">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-gray-400">Projekte</CardTitle>
              <FolderOpen className="h-4 w-4 text-[#C4A55A]" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-white">{stats?.projectsCount || 0}</div>
              <p className="text-xs text-gray-500 mt-1">Aktive Projekte</p>
            </CardContent>
          </Card>

          <Card className="bg-[#1a1a1a] border-[#C4A55A]/20 hover:border-[#C4A55A]/50 transition-all duration-300">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-gray-400">Nachrichten</CardTitle>
              <Mail className="h-4 w-4 text-[#C4A55A]" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-white">{stats?.messagesCount || 0}</div>
              <p className="text-xs text-gray-500 mt-1">Kontaktanfragen</p>
            </CardContent>
          </Card>
        </div>
        )}

        {/* Quick Actions */}
        <Card className="bg-[#1a1a1a] border-[#C4A55A]/20">
          <CardHeader>
            <CardTitle className="text-white">Schnellzugriff</CardTitle>
            <CardDescription className="text-gray-400">
              Verwalten Sie Ihre Website-Inhalte
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Button
                onClick={() => navigate('/admin/projects')}
                className="h-24 bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] border border-[#C4A55A]/30 hover:border-[#C4A55A] text-white hover:bg-[#C4A55A]/10 transition-all duration-300"
              >
                <div className="flex flex-col items-center gap-2">
                  <FolderOpen className="h-8 w-8 text-[#C4A55A]" />
                  <span>Projekte verwalten</span>
                </div>
              </Button>

              <Button
                onClick={() => navigate('/admin/downloads')}
                data-testid="admin-dashboard-downloads-btn"
                className="h-24 bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] border border-[#C4A55A]/30 hover:border-[#C4A55A] text-white hover:bg-[#C4A55A]/10 transition-all duration-300"
              >
                <div className="flex flex-col items-center gap-2">
                  <FileText className="h-8 w-8 text-[#C4A55A]" />
                  <span>Downloads verwalten</span>
                </div>
              </Button>

              <Button
                onClick={() => navigate('/admin/blog')}
                data-testid="admin-dashboard-blog-btn"
                className="h-24 bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] border border-[#C4A55A]/30 hover:border-[#C4A55A] text-white hover:bg-[#C4A55A]/10 transition-all duration-300"
              >
                <div className="flex flex-col items-center gap-2">
                  <FileText className="h-8 w-8 text-[#C4A55A]" />
                  <span>Blog-Beiträge</span>
                </div>
              </Button>

              {isAdmin && (
                <>
                  <Button
                    onClick={() => navigate('/admin/subscriptions')}
                    data-testid="admin-dashboard-subscriptions-btn"
                    className="h-24 bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] border border-[#C4A55A]/30 hover:border-[#C4A55A] text-white hover:bg-[#C4A55A]/10 transition-all duration-300"
                  >
                    <div className="flex flex-col items-center gap-2">
                      <Coins className="h-8 w-8 text-[#C4A55A]" />
                      <span>Zeichnungen prüfen</span>
                    </div>
                  </Button>

                  <Button
                    onClick={() => navigate('/admin/users')}
                    className="h-24 bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] border border-[#C4A55A]/30 hover:border-[#C4A55A] text-white hover:bg-[#C4A55A]/10 transition-all duration-300"
                  >
                    <div className="flex flex-col items-center gap-2">
                      <Users className="h-8 w-8 text-[#C4A55A]" />
                      <span>Investoren ansehen</span>
                    </div>
                  </Button>

                  <Button
                    onClick={() => navigate('/admin/shareholders')}
                    data-testid="admin-dashboard-shareholders-btn"
                    className="h-24 bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] border border-[#C4A55A]/30 hover:border-[#C4A55A] text-white hover:bg-[#C4A55A]/10 transition-all duration-300"
                  >
                    <div className="flex flex-col items-center gap-2">
                      <Briefcase className="h-8 w-8 text-[#C4A55A]" />
                      <span>Aktionärs-Register</span>
                    </div>
                  </Button>

                  <Button
                    onClick={() => navigate('/admin/messages')}
                    className="h-24 bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] border border-[#C4A55A]/30 hover:border-[#C4A55A] text-white hover:bg-[#C4A55A]/10 transition-all duration-300"
                  >
                    <div className="flex flex-col items-center gap-2">
                      <Mail className="h-8 w-8 text-[#C4A55A]" />
                      <span>Nachrichten lesen</span>
                    </div>
                  </Button>

                  <Button
                    onClick={() => navigate('/admin/team')}
                    data-testid="admin-dashboard-team-btn"
                    className="h-24 bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] border border-[#C4A55A]/30 hover:border-[#C4A55A] text-white hover:bg-[#C4A55A]/10 transition-all duration-300"
                  >
                    <div className="flex flex-col items-center gap-2">
                      <UserCog className="h-8 w-8 text-[#C4A55A]" />
                      <span>Team verwalten</span>
                    </div>
                  </Button>

                  <Button
                    onClick={() => navigate('/admin/mediators')}
                    data-testid="admin-dashboard-mediators-btn"
                    className="h-24 bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] border border-[#C4A55A]/30 hover:border-[#C4A55A] text-white hover:bg-[#C4A55A]/10 transition-all duration-300"
                  >
                    <div className="flex flex-col items-center gap-2">
                      <NetworkIcon className="h-8 w-8 text-[#C4A55A]" />
                      <span>Vermittler</span>
                    </div>
                  </Button>

                  <Button
                    onClick={() => navigate('/admin/properties')}
                    data-testid="admin-dashboard-properties-btn"
                    className="h-24 bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] border border-[#C4A55A]/30 hover:border-[#C4A55A] text-white hover:bg-[#C4A55A]/10 transition-all duration-300"
                  >
                    <div className="flex flex-col items-center gap-2">
                      <Building2 className="h-8 w-8 text-[#C4A55A]" />
                      <span>Immobilien</span>
                    </div>
                  </Button>

                  <Button
                    onClick={() => navigate('/admin/valuation')}
                    data-testid="admin-dashboard-valuation-btn"
                    className="h-24 bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] border border-[#C4A55A]/30 hover:border-[#C4A55A] text-white hover:bg-[#C4A55A]/10 transition-all duration-300"
                  >
                    <div className="flex flex-col items-center gap-2">
                      <Calculator className="h-8 w-8 text-[#C4A55A]" />
                      <span>DCF-Bewertung</span>
                    </div>
                  </Button>

                  <Button
                    onClick={() => navigate('/admin/settings')}
                    data-testid="admin-dashboard-settings-btn"
                    className="h-24 bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] border border-[#C4A55A]/30 hover:border-[#C4A55A] text-white hover:bg-[#C4A55A]/10 transition-all duration-300"
                  >
                    <div className="flex flex-col items-center gap-2">
                      <Settings className="h-8 w-8 text-[#C4A55A]" />
                      <span>Einstellungen</span>
                    </div>
                  </Button>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};